import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-json-list-input',
  templateUrl: './json-list-input.component.html',
  styleUrls: ['./json-list-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonListInputComponent),
      multi: true
    }
  ]
})
export class JsonListInputComponent implements ControlValueAccessor {
  @Input() dataType: string | null = null;
  @Input() placeholder: string = 'Enter Default Value';
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() variableName: string = '';

  value: any = '';
  displayValue: string = '';

  showModal: boolean = false;
  modalValue: string = '';
  modalError: string = '';

  onChange: any = () => { };
  onTouched: any = () => { };

  get normalizedDataType(): string {
    return (this.dataType || '').toString().toLowerCase();
  }

  get isJsonOrList(): boolean {
    const type = this.normalizedDataType;
    return type === 'json' || type === 'list';
  }

  get effectivePlaceholder(): string {
    return this.isJsonOrList ? 'Click to edit...' : this.placeholder;
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateDisplayValue();
  }

  updateDisplayValue(): void {
    if (this.value === null || this.value === undefined || this.value === '') {
      this.displayValue = '';
      return;
    }

    if (this.normalizedDataType === 'json') {
      this.displayValue = String(this.value);
    } else if (this.normalizedDataType === 'list') {
      try {
        const parsed = typeof this.value === 'string' ? JSON.parse(this.value) : this.value;
        this.displayValue = this.formatList(parsed);
      } catch (e) {
        this.displayValue = String(this.value);
      }
    } else {
      this.displayValue = String(this.value);
    }
  }

  private formatList(value: any): string {
    if (Array.isArray(value)) {
      const items = value.map(item => {
        if (Array.isArray(item)) {
          return this.formatList(item); // recursive for nested lists
        } else if (typeof item === 'string') {
          return `'${item}'`;
        } else {
          return String(item);
        }
      });
      return `[${items.join(', ')}]`;
    }
    return String(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    if (!this.isJsonOrList) {
      const input = event.target as HTMLInputElement;
      this.value = input.value;
      this.onChange(this.value);
    }
  }

  openEditor(): void {
    if (!this.isJsonOrList || this.disabled || this.readonly) {
      return;
    }

    if (this.normalizedDataType === 'json') {
      if (this.value) {
        try {
          const parsed = JSON.parse(this.value);
          this.modalValue = JSON.stringify(parsed, null, 2);
        } catch (e) {
          this.modalValue = this.value;
        }
      } else {
        this.modalValue = '';
      }
    } else if (this.normalizedDataType === 'list') {
      if (this.value) {
        try {
          const parsed = JSON.parse(this.value);
          if (Array.isArray(parsed)) {
            this.modalValue = this.formatList(parsed);
          } else {
            this.modalValue = this.value;
          }
        } catch (e) {
          this.modalValue = this.value;
        }
      } else {
        this.modalValue = '[]';
      }
    }

    this.modalError = '';
    this.showModal = true;
  }

  validateInput(): void {
    this.modalError = '';

    if (!this.modalValue.trim()) {
      return;
    }

    if (this.normalizedDataType === 'json') {
      try {
        JSON.parse(this.modalValue);
      } catch (e) {
        this.modalError = 'Invalid JSON format';
      }
    } else if (this.normalizedDataType === 'list') {
      const trimmed = this.modalValue.trim();

      if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
        this.modalError = "Invalid list format. Must use brackets: ['a', 12] or [[1,2],[3,4]]";
        return;
      }

      try {
        const jsonString = trimmed.replace(/'/g, '"');
        JSON.parse(jsonString);
      } catch (e) {
        this.modalError = "Invalid list format. Use: ['a', 12] or [[1,2],[3,4]]";
      }
    }
  }

  saveValue(): void {
    this.validateInput();

    if (this.modalError) {
      return;
    }

    if (this.normalizedDataType === 'json') {
      if (this.modalValue.trim()) {
        try {
          this.value = this.modalValue.trim();
        } catch (e) {
          this.value = this.modalValue;
        }
      } else {
        this.value = '';
      }
    } else if (this.normalizedDataType === 'list') {
      if (this.modalValue.trim()) {
        const trimmed = this.modalValue.trim();
        try {
          const jsonString = trimmed.replace(/'/g, '"');
          const parsed = JSON.parse(jsonString);
          this.value = JSON.stringify(parsed);
        } catch (e) {
          this.modalError = 'Failed to parse list';
          return;
        }
      } else {
        this.value = '';
      }
    } else {
      this.value = this.modalValue;
    }

    this.updateDisplayValue();
    this.onChange(this.value);
    this.onTouched();
    this.closeModal();
  }

  closeModal(): void {
    this.showModal = false;
    this.modalValue = '';
    this.modalError = '';
  }

  getModalTitle(): string {
    const varName = this.variableName || 'value';
    return this.normalizedDataType === 'json'
      ? `Edit JSON - ${varName}`
      : `Edit List - ${varName}`;
  }

  getModalPlaceholder(): string {
    if (this.normalizedDataType === 'json') {
      return '{\n  "key": "value"\n}';
    }
    return "['item1', 12, 'item3'] or [[1,2],[3,4]]";
  }

  onInputClick(): void {
    if (this.isJsonOrList) {
      this.openEditor();
    }
  }

  beautifyJson() {
    if (!this.modalValue) return;

    try {
      const obj = JSON.parse(this.modalValue);
      this.modalValue = JSON.stringify(obj, null, 2);
      this.modalError = '';
    } catch (e) {
      this.modalError = 'Invalid JSON. Cannot beautify.';
    }
  }

  getModalLabel(): string {
    const varName = this.variableName || 'value';
    return this.normalizedDataType === 'json'
      ? `Enter JSON for variable: ${varName}`
      : `Enter List for variable: ${varName}`;
  }
}